import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getGalleryItems, getMyContent, getMyContentItem, shareToGallery, toggleGalleryLike, toggleGalleryFavorite, getGalleryItem, markGalleryItemUsed, deleteGalleryItem, trackGalleryDownload, updateGalleryItem, updateMyContent, deleteMyContent } from '../../api/galleryApi';
import { downloadAndOpen } from '../../utils/downloadUtils';
import useFileDownload from '../../hooks/useFileDownload';
import GalleryFamilyActions from '../../components/gallery/GalleryFamilyActions';
import LibraryArtifactActions from '../../components/library/LibraryArtifactActions';
import LibraryDownloadMenu from '../../components/library/LibraryDownloadMenu';
import LibraryFamilyPreview, { LibraryFamilyCarousel } from '../../components/library/LibraryFamilyPreview';
import { LibraryFamilyBadge } from '../../components/library/LibraryVersionSwitch';
import useLibraryFamilyView from '../../components/library/useLibraryFamilyView';
import { familyCardThumbnail, familyHasVersion, familyPageLabel } from '../../utils/libraryFamily';
import { formatLibraryTimestamp, sortLibraryItemsByUpdatedAt } from '../../utils/libraryTimestamps';
import { prepareTeacherReferralShareUrl } from '../../api/inviteApi';
import { auth } from '../../firebase/config';
import { useToast } from '../../components/Toast';
import { useConfirm } from '../../components/common/ConfirmDialog';
import Dialog from '../../components/common/Dialog';
import ModalActions from '../../components/common/ModalActions';
import { Spinner } from '../../components/common/AsyncState';
import LoginPrompt from '../../Auth/LoginPrompt';
import { trackInviteShare } from '../../utils/eventTracker';
import { shareContent } from '../../utils/shareUtils';
import HtmlSlideFullscreen from '../../components/chat/slides/HtmlSlideFullscreen';
import LibraryPresentation from '../../components/library/LibraryPresentation';
import { canPresentLibraryPpt } from '../../components/chat/presentationSlides';
import { galleryItemAsFamily } from '../../utils/galleryFamilyItem';
import { buildLibraryShareSourceData } from '../../utils/galleryShareFamily';
import {
  LIBRARY_LIST_PAGE_SIZE,
  applyLibraryListPage,
  buildLibraryListKey,
  emptyLibraryLists,
  patchLibraryListItem,
  refreshLibraryListHead,
  removeLibraryListItem,
} from '../../utils/libraryListState';
import { gallerySharePath } from '../../utils/galleryShareFeedback';
import useGalleryShare from '../../hooks/useGalleryShare';
import useAutoLoadMore from '../../hooks/useAutoLoadMore';
import LibraryShareToGalleryButton from '../../components/library/LibraryShareToGalleryButton';
import { defaultFamilyViewMode } from '../../utils/libraryFamily';
import {
  LIBRARY_SCHOOL_LEVELS,
  LIBRARY_SUBJECTS,
  getLibrarySchoolLabel,
  getLibrarySubjectFilters,
  getLibrarySubjectLabel,
} from '../../data/libraryTaxonomy';
import './GalleryPage.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const MAIN_TABS = [
  { id: 'my', label: '내 자료', requiresAuth: true },
  { id: 'nanumteo', label: '나눔터' },
  { id: 'favorites', label: '즐겨찾기', requiresAuth: true },
];

const CONTENT_TYPE_FILTERS = [
  { id: 'all', label: '전체' },
  { id: 'worksheet', label: '학습지' },
  { id: 'ppt', label: 'PPT' },
  { id: 'image', label: '이미지' },
  { id: 'other', label: '기타' },
];

const VALID_CONTENT_TYPES = new Set(CONTENT_TYPE_FILTERS.map(({ id }) => id));
const VALID_SCHOOL_LEVELS = new Set(LIBRARY_SCHOOL_LEVELS.map(({ id }) => id));
const VALID_SUBJECTS = new Set(['all', ...Object.keys(LIBRARY_SUBJECTS)]);

const SORT_OPTIONS = [
  { id: 'latest', label: '최신순' },
  { id: 'popular', label: '인기순' },
];

const VALID_MAIN_TABS = new Set(MAIN_TABS.map(({ id }) => id));
const MAX_SEARCH_LENGTH = 100;

// 탭·필터·정렬·검색어는 URL이 기준이다. 뒤로/앞으로 가기로 URL만 바뀌어도 화면이 같은 값으로 돌아온다.
const parseLibraryParams = (searchParams) => {
  const tabParam = searchParams.get('tab');
  const typeParam = searchParams.get('type') || 'all';
  const schoolParam = searchParams.get('school') || 'all';
  const subjectParam = searchParams.get('subject') || 'all';
  const sortParam = searchParams.get('sort') || 'latest';
  const schoolLevel = VALID_SCHOOL_LEVELS.has(schoolParam) ? schoolParam : 'all';
  const subjectIsAvailable = getLibrarySubjectFilters(schoolLevel).some(({ id }) => id === subjectParam);
  return {
    mainTab: VALID_MAIN_TABS.has(tabParam) ? tabParam : (auth.currentUser ? 'my' : 'nanumteo'),
    typeFilter: VALID_CONTENT_TYPES.has(typeParam) ? typeParam : 'all',
    schoolLevel,
    subject: VALID_SUBJECTS.has(subjectParam) && subjectIsAvailable ? subjectParam : 'all',
    sort: SORT_OPTIONS.some(({ id }) => id === sortParam) ? sortParam : 'latest',
    search: (searchParams.get('q') || '').trim().slice(0, MAX_SEARCH_LENGTH),
    mineId: searchParams.get('mine') || null,
  };
};

const TYPE_BADGE = {
  ppt: { label: 'PPT', bg: '#DBEAFE', color: '#2563EB' },
  worksheet: { label: '학습지', bg: '#D1FAE5', color: '#059669' },
  image: { label: '이미지', bg: '#FEF3C7', color: '#D97706' },
  other: { label: '기타', bg: '#F2F4F6', color: '#4E5968' },
};

const getOtherFormatLabel = (item) => String(
  item?.artifact_format || item?.source_data?.format || 'FILE'
).toUpperCase();

const OtherFilePlaceholder = ({ item }) => (
  <div className="gallery-card-file-placeholder">
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#6B7684" strokeWidth="1.5">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
    </svg>
    <span>{getOtherFormatLabel(item)}</span>
  </div>
);

const GalleryCard = ({ item, onLike, onFavorite, onClick }) => {
  const badge = TYPE_BADGE[item.content_type] || TYPE_BADGE.ppt;
  return (
    <div className="gallery-card" onClick={() => onClick(item)}>
      <div className="gallery-card-thumb">
        {item.thumbnail_url ? (
          <img src={item.thumbnail_url} alt={item.title} loading="lazy" />
        ) : item.content_type === 'other' ? (
          <OtherFilePlaceholder item={item} />
        ) : (
          <div className="gallery-card-thumb-placeholder">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/>
            </svg>
          </div>
        )}
        <span className="gallery-card-badge" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
      </div>
      <div className="gallery-card-body">
        <p className="gallery-card-title">{item.title}</p>
        <p className="gallery-card-taxonomy">
          {getLibrarySchoolLabel(item.school_level)}
          <span aria-hidden="true"> · </span>
          {getLibrarySubjectLabel(item.subject)}
          {item.grade_tags?.length > 0 && (
            <>
              <span aria-hidden="true"> · </span>
              {item.grade_tags.join('·')}
            </>
          )}
        </p>
        <div className="gallery-card-meta">
          <button type="button" aria-pressed={Boolean(item.is_liked)} aria-label={`${item.title} 좋아요`} className={`gallery-card-like ${item.is_liked ? 'gallery-card-like--active' : ''}`} onClick={(e) => { e.stopPropagation(); onLike(item); }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill={item.is_liked ? '#EF4444' : 'none'} stroke={item.is_liked ? '#EF4444' : 'currentColor'} strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
            {item.like_count > 0 && <span>{item.like_count}</span>}
          </button>
          <button type="button" aria-pressed={Boolean(item.is_favorited)} aria-label={`${item.title} 즐겨찾기`} className={`gallery-card-fav ${item.is_favorited ? 'gallery-card-fav--active' : ''}`} onClick={(e) => { e.stopPropagation(); onFavorite(item); }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill={item.is_favorited ? '#3182F6' : 'none'} stroke={item.is_favorited ? '#3182F6' : 'currentColor'} strokeWidth="2">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
            </svg>
          </button>
          <span className="gallery-card-stat-group">
            <span className="gallery-card-stat">활용 {item.use_count}</span>
            <span className="gallery-card-stat gallery-card-stat--download">다운 {item.download_count || 0}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

const formatFileSize = (sizeBytes) => {
  const size = Number(sizeBytes) || 0;
  if (size <= 0) return '';
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
};

const openLibraryPresentation = (presentable, setPresentation, setPresentChoices) => {
  const versions = presentable?.versions || [];
  if (versions.length > 1) {
    setPresentChoices(versions);
    return;
  }
  if (presentable?.slides?.length) setPresentation({ slides: presentable.slides, deckId: presentable.deckId });
};

const MyContentCard = ({ item, onClick }) => {
  const badge = TYPE_BADGE[item.content_type] || TYPE_BADGE.ppt;
  const presentable = canPresentLibraryPpt(item);
  const [presentation, setPresentation] = useState(null);
  const [presentChoices, setPresentChoices] = useState(null);
  const thumb = familyCardThumbnail(item);
  const pageLabel = familyPageLabel(item);
  return (
    <>
    <div className="gallery-card" onClick={() => onClick(item)} style={{ cursor: 'pointer' }}>
      <div className="gallery-card-thumb">
        {thumb ? (
          <img src={thumb} alt={item.title} loading="lazy" />
        ) : item.content_type === 'other' ? (
          <OtherFilePlaceholder item={item} />
        ) : (
          <div className="gallery-card-thumb-placeholder">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/>
            </svg>
          </div>
        )}
        <span className="gallery-card-badge" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
        <LibraryFamilyBadge className="gallery-card-family-badge" item={item} />
        {item.is_shared && (
          <span className="gallery-card-badge" style={{ background: '#DBEAFE', color: '#2563EB', right: 'auto', left: 8, top: 'auto', bottom: 8, position: 'absolute' }}>공유됨</span>
        )}
      </div>
      <div className="gallery-card-body">
        <p className="gallery-card-title">{item.title}</p>
        {pageLabel ? <p className="gallery-card-desc">{pageLabel}</p> : null}
        <div className="gallery-card-meta" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>{formatLibraryTimestamp(item)}</span>
          {presentable.ok && (
            <button
              className="gallery-card-present"
              data-testid="wqpresent-library-card"
              onClick={(event) => {
                event.stopPropagation();
                openLibraryPresentation(presentable, setPresentation, setPresentChoices);
              }}
              type="button"
            >
              발표 모드
            </button>
          )}
        </div>
      </div>
    </div>
      <LibraryDownloadMenu
        onClose={() => setPresentChoices(null)}
        onSelect={(option) => {
          setPresentChoices(null);
          if (option?.slides?.length) setPresentation({ slides: option.slides, deckId: option.deckId });
        }}
        open={Boolean(presentChoices?.length)}
        options={presentChoices || []}
        testId="wqpresent-version-menu"
        title="어떤 버전으로 발표할까요?"
      />
      {presentation?.slides?.length > 0 && (
        <LibraryPresentation onClose={() => setPresentation(null)} presentation={presentation} />
      )}
    </>
  );
};

const MyContentDetailModal = ({
  item,
  isSharing = false,
  onClose,
  onShare,
  onViewShared,
  onContinueInChat,
  onUpdate,
  onDelete,
  onDetailLoaded,
}) => {
  const [linkCopied, setLinkCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [fullscreenIdx, setFullscreenIdx] = useState(null);
  const [presentation, setPresentation] = useState(null);
  const [presentChoices, setPresentChoices] = useState(null);
  const [detail, setDetail] = useState(item);
  const [viewMode, setViewMode] = useState(defaultFamilyViewMode(item));
  const [pageKind, setPageKind] = useState('student');
  const userPickedViewModeRef = useRef(false);
  const itemRef = useRef(item);
  const onDetailLoadedRef = useRef(onDetailLoaded);
  itemRef.current = item;
  onDetailLoadedRef.current = onDetailLoaded;
  const { showToast } = useToast();
  const openConfirm = useConfirm();
  const raw = detail || item;
  const view = {
    ...raw,
    is_shared: Boolean(raw?.is_shared || item?.is_shared),
    shared_gallery_id: raw?.shared_gallery_id || item?.shared_gallery_id || null,
  };
  const familyView = useLibraryFamilyView(view, viewMode, pageKind);

  // 칸 id가 바뀔 때만 상세를 다시 받는다. 같은 칸의 item/onDetailLoaded 참조 변경은 탭을 덮지 않는다.
  useEffect(() => {
    const current = itemRef.current;
    userPickedViewModeRef.current = false;
    setLinkCopied(false);
    setIsEditing(false);
    setFullscreenIdx(null);
    setPresentation(null);
    setPresentChoices(null);
    setDetail(current);
    setViewMode(defaultFamilyViewMode(current));
    setPageKind('student');
    if (!current?.id || (current.content_type !== 'ppt' && current.content_type !== 'worksheet')) {
      return undefined;
    }
    let cancelled = false;
    getMyContentItem(current.id)
      .then((full) => {
        if (cancelled || !full) return;
        setDetail(full);
        if (!userPickedViewModeRef.current) {
          setViewMode(defaultFamilyViewMode(full));
        }
        onDetailLoadedRef.current?.(full);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [item?.id]);

  const handleViewModeChange = (mode) => {
    userPickedViewModeRef.current = true;
    setViewMode(mode);
  };

  if (!item || !view) return null;

  const badge = TYPE_BADGE[view.content_type] || TYPE_BADGE.ppt;
  const fileUrl = view.download_url || view.thumbnail_url;
  const canDownload = Boolean(fileUrl || view.artifact_id || familyView.isFamily);
  const isOtherFile = view.content_type === 'other';
  const outline = familyView.outline;

  const handleShareLink = async () => {
    let shareUrl;

    if (view.is_shared && view.shared_gallery_id) {
      shareUrl = `${window.location.origin}/library/${view.shared_gallery_id}?tab=nanumteo`;
    } else {
      if (isOtherFile) {
        const ok = await openConfirm({
          title: '나눔터에 공개하고 링크를 만들까요?',
          description: '공유 후에는 누구나 다운로드할 수 있어요.\n민감한 정보가 없는지 먼저 확인해주세요',
          confirmLabel: '공유하기',
        });
        if (!ok) return;
      }
      try {
        const result = await onShare(view, true);
        if (result?.id) {
          shareUrl = `${window.location.origin}/library/${result.id}?tab=nanumteo`;
        }
      } catch (_) {}
    }

    if (!shareUrl) shareUrl = fileUrl;
    if (!shareUrl) return;

    const referralShare = await prepareTeacherReferralShareUrl({
      user: auth.currentUser,
      url: shareUrl,
      surface: 'library',
      audience: 'teacher',
    });
    const result = await shareContent({
      title: view.title,
      text: '왓퀴즈 나눔터에서 이 수업 자료를 확인해보세요.',
      url: referralShare.url,
    });
    if (result.success && result.method === 'clipboard') {
      trackInviteShare(
        'library',
        result.method,
        referralShare.referralAttached,
      );
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } else if (result.success) {
      trackInviteShare(
        'library',
        result.method,
        referralShare.referralAttached,
      );
    }
  };

  const startEditing = () => {
    setEditTitle(view.title || '');
    setIsEditing(true);
  };

  const saveEditing = async () => {
    if (!editTitle.trim()) return;
    setEditSaving(true);
    try {
      await onUpdate(view.id, { title: editTitle.trim() });
      setIsEditing(false);
    } catch (e) {
      showToast('제목을 수정하지 못했어요. 잠시 후 다시 시도해주세요', 'error');
    } finally {
      setEditSaving(false);
    }
  };

  // 받기·이어 수정은 LibraryArtifactActions로 분리한다. 발표 모드는 extra 슬롯으로 붙인다.
  const presentable = canPresentLibraryPpt(view, outline, viewMode ? { mode: viewMode } : {});

  return (
    <>
      <Dialog
        aria-label={`${view.title} 자료 상세`}
        closeLabel="자료 상세 닫기"
        dismissible={fullscreenIdx === null && !presentation?.slides?.length && !presentChoices?.length}
        flush
        onClose={onClose}
        open
        panelClassName="gallery-detail-modal"
        size="lg"
        testId="gallery-my-detail"
      >
        <LibraryFamilyCarousel
          resetKey={`${view.id}-${viewMode}-${pageKind}`}
          title={view.title}
          urls={familyView.allPreviews}
        />

        <div className="gallery-detail-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span className="gallery-detail-badge" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
            <LibraryFamilyBadge item={view} />
            {view.is_shared && (
              <span className="gallery-detail-badge" style={{ background: '#DBEAFE', color: '#2563EB' }}>나눔터 공유됨</span>
            )}
            <span style={{ fontSize: 13, color: '#9CA3AF', marginLeft: 'auto' }}>{formatLibraryTimestamp(view)}</span>
          </div>

          {isEditing ? (
            <div className="gallery-detail-edit-form">
              <input
                className="gallery-detail-edit-title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                maxLength={100}
                placeholder="제목"
                aria-label="자료 제목"
                autoFocus
              />
              <div className="gallery-detail-edit-actions">
                <button type="button" className="gallery-detail-edit-cancel" onClick={() => setIsEditing(false)} disabled={editSaving}>취소</button>
                <button type="button" className="gallery-detail-edit-save" onClick={saveEditing} disabled={editSaving || !editTitle.trim()}>
                  {editSaving ? '저장하는 중' : '저장하기'}
                </button>
              </div>
            </div>
          ) : (
            <div className="gallery-detail-title-row">
              <h2 className="gallery-detail-title">{view.title}</h2>
              <button type="button" className="gallery-detail-edit-btn" onClick={startEditing} title="제목 수정" aria-label="제목 수정">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
            </div>
          )}

          {isOtherFile && (
            <div className="gallery-detail-file-info">
              <span>{getOtherFormatLabel(view)}</span>
              {Number(view.size_bytes) > 0 && (
                <span>{formatFileSize(view.size_bytes)}</span>
              )}
              {view.filename && <span>{view.filename}</span>}
            </div>
          )}

          <LibraryFamilyPreview
            item={view}
            onOpenFullscreen={setFullscreenIdx}
            onPageKindChange={setPageKind}
            onViewModeChange={handleViewModeChange}
            pageKind={pageKind}
            view={familyView}
            viewMode={viewMode}
          />

          <LibraryArtifactActions
            canDownload={canDownload}
            extra={presentable.ok ? (
              <button
                className="gallery-detail-text-btn"
                data-testid="wqpresent-library-detail"
                onClick={() => openLibraryPresentation(presentable, setPresentation, setPresentChoices)}
                type="button"
              >
                발표 모드
              </button>
            ) : null}
            item={view}
            onContinueInChat={onContinueInChat}
            testId="gallery-my-detail-actions"
          />

          <div className="gallery-detail-sub-actions">
            {canDownload && (
              <button type="button" className="gallery-detail-text-btn" disabled={isSharing} onClick={handleShareLink} style={linkCopied ? { color: '#10B981' } : undefined}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={linkCopied ? '#10B981' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {linkCopied ? (
                    <polyline points="20,6 9,17 4,12"/>
                  ) : (
                    <><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></>
                  )}
                </svg>
                {linkCopied ? '복사했어요' : '링크 공유'}
              </button>
            )}
            <LibraryShareToGalleryButton
              galleryId={view.shared_gallery_id}
              isShared={Boolean(view.is_shared)}
              isSharing={isSharing}
              onShare={() => onShare(view)}
              onView={onViewShared}
            />
          </div>

          <button type="button" className="gallery-detail-delete-btn" onClick={() => onDelete(view.id)}>
            이 자료 삭제하기
          </button>
        </div>
      </Dialog>

      {/* PPT 슬라이드 풀스크린 미리보기 (좌우 이동) — 라이브러리 컨텍스트: 편집/발표 버튼 숨김, 빈공간 닫기 방지.
          body로 포털되어 Dialog 위(z-index 100000)에 뜨며, 열려 있는 동안 상세 Dialog는 ESC·스크림으로 닫히지 않는다 */}
      {fullscreenIdx !== null && familyView.fullscreenSlides.length > 0 && (
        <HtmlSlideFullscreen
          slides={familyView.fullscreenSlides}
          initialIndex={fullscreenIdx}
          onClose={() => setFullscreenIdx(null)}
          hideEditButton
          hidePresentButton
          themeId={familyView.fullscreenThemeId}
        />
      )}
      <LibraryDownloadMenu
        onClose={() => setPresentChoices(null)}
        onSelect={(option) => {
          setPresentChoices(null);
          if (option?.slides?.length) setPresentation({ slides: option.slides, deckId: option.deckId });
        }}
        open={Boolean(presentChoices?.length)}
        options={presentChoices || []}
        testId="wqpresent-version-menu"
        title="어떤 버전으로 발표할까요?"
      />
      {presentation?.slides?.length > 0 && (
        <LibraryPresentation onClose={() => setPresentation(null)} presentation={presentation} />
      )}
    </>
  );
};

// 카드의 요약 정보로 먼저 열어 두는 상태. 원본 데이터(source_data 등)가 없어서 가져오기·다운로드는 잠근다.
const GalleryDetailPreview = ({ item }) => (
  <>
    <div className="gallery-detail-preview-thumb">
      {item.thumbnail_url
        ? <img src={item.thumbnail_url} alt={item.title} />
        : <div className="gallery-detail-preview-thumb-empty gallery-skeleton-shimmer" />}
    </div>
    <div className="gallery-detail-body">
      <h2 className="gallery-detail-title">{item.title}</h2>
      <div className="gallery-detail-preview-lines" aria-hidden="true">
        <div className="gallery-skeleton-line gallery-skeleton-shimmer" style={{ width: '60%' }} />
        <div className="gallery-skeleton-line gallery-skeleton-shimmer" style={{ width: '40%' }} />
      </div>
      <ModalActions
        primary={{ label: '자료를 불러오고 있어요', loading: true, disabled: true, onClick: () => {} }}
        testId="gallery-detail-actions"
      />
    </div>
  </>
);

const GalleryDetailModal = ({ item, loading = false, onClose, onUse, onLike, onFavorite, onDelete, onShare, onDownload, onUpdate, onTrackDownload, downloadBusy = false, downloadLabel = null }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [fullscreenIdx, setFullscreenIdx] = useState(null);
  const [familyOverlay, setFamilyOverlay] = useState(false);
  const family = galleryItemAsFamily(item);
  const [viewMode, setViewMode] = useState(defaultFamilyViewMode(family));
  const [pageKind, setPageKind] = useState('student');
  const itemRef = useRef(item);
  itemRef.current = item;
  const { showToast } = useToast();
  const familyView = useLibraryFamilyView(family, viewMode, pageKind);

  // 같은 글의 item 참조만 바뀌면(좋아요·제목 수정) 사용자가 고른 탭을 덮지 않는다.
  // 요약 → 원본으로 바뀔 때 기본 탭을 다시 고르는데, 그리기 전에 끝내야 먼저 누른 탭을 덮지 않는다.
  useLayoutEffect(() => {
    const current = itemRef.current;
    setIsEditing(false);
    setFullscreenIdx(null);
    setFamilyOverlay(false);
    setViewMode(defaultFamilyViewMode(galleryItemAsFamily(current)));
    setPageKind('student');
  }, [item?.id, loading]);

  if (!item) return null;
  const badge = TYPE_BADGE[item.content_type] || TYPE_BADGE.ppt;
  const fileUrl = item.source_data?.downloadUrl || item.download_url || item.thumbnail_url;

  const startEditing = () => {
    setEditTitle(item.title || '');
    setEditDesc(item.description || '');
    setIsEditing(true);
  };

  const cancelEditing = () => { setIsEditing(false); };

  const saveEditing = async () => {
    if (!editTitle.trim()) return;
    setEditSaving(true);
    try {
      await onUpdate(item.id, { title: editTitle.trim(), description: editDesc.trim() || null });
      setIsEditing(false);
    } catch (e) {
      showToast('자료 정보를 수정하지 못했어요. 잠시 후 다시 시도해주세요', 'error');
    } finally {
      setEditSaving(false);
    }
  };

  const primaryAction = item.content_type === 'other'
    ? {
        label: downloadBusy ? (downloadLabel || '파일 준비 중…') : '파일 다운로드',
        onClick: () => onDownload(fileUrl, item.id, item.content_type, item.title),
        disabled: !fileUrl || downloadBusy,
        loading: downloadBusy,
      }
    : { label: '내 것으로 가져와 이어서 수정', onClick: () => onUse(item) };

  return (
    <>
      <Dialog
        aria-busy={loading || undefined}
        aria-label={`${item.title} 자료 상세`}
        closeLabel="자료 상세 닫기"
        dismissible={fullscreenIdx === null && !familyOverlay}
        flush
        onClose={onClose}
        open
        panelClassName="gallery-detail-modal"
        size="lg"
        testId="gallery-detail"
      >
        {loading ? <GalleryDetailPreview item={item} /> : (
        <>
        <LibraryFamilyCarousel
          resetKey={`${item.id}-${viewMode}-${pageKind}`}
          title={item.title}
          urls={familyView.allPreviews}
        />

        <div className="gallery-detail-body">
          <div className="gallery-detail-classification">
            <span className="gallery-detail-badge" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
            <LibraryFamilyBadge item={family} />
            <span className="gallery-detail-taxonomy">
              {getLibrarySchoolLabel(item.school_level)}
              <span aria-hidden="true"> · </span>
              {getLibrarySubjectLabel(item.subject)}
              {item.grade_tags?.length > 0 && (
                <>
                  <span aria-hidden="true"> · </span>
                  {item.grade_tags.join('·')}
                </>
              )}
            </span>
            <span style={{ fontSize: 13, color: '#9CA3AF', marginLeft: 'auto' }}>{formatLibraryTimestamp(family)}</span>
          </div>
          {isEditing ? (
            <div className="gallery-detail-edit-form">
              <input
                className="gallery-detail-edit-title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                maxLength={100}
                placeholder="제목"
                autoFocus
              />
              <textarea
                className="gallery-detail-edit-desc"
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                maxLength={200}
                placeholder="설명 (선택)"
                rows={2}
              />
              <div className="gallery-detail-edit-actions">
                <button type="button" className="gallery-detail-edit-cancel" onClick={cancelEditing} disabled={editSaving}>취소</button>
                <button type="button" className="gallery-detail-edit-save" onClick={saveEditing} disabled={editSaving || !editTitle.trim()}>
                  {editSaving ? '저장하는 중' : '저장하기'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="gallery-detail-title-row">
                <h2 className="gallery-detail-title">{item.title}</h2>
                {item.is_mine && (
                  <button type="button" className="gallery-detail-edit-btn" onClick={startEditing} title="수정" aria-label="자료 정보 수정">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                )}
              </div>
              {item.description && <p className="gallery-detail-desc">{item.description}</p>}
            </>
          )}

          {item.content_type === 'other' && (
            <div className="gallery-detail-file-info">
              <span>{getOtherFormatLabel(item)}</span>
              {Number(item.source_data?.sizeBytes) > 0 && (
                <span>{formatFileSize(item.source_data.sizeBytes)}</span>
              )}
              {item.source_data?.filename && <span>{item.source_data.filename}</span>}
            </div>
          )}

          <LibraryFamilyPreview
            item={family}
            onOpenFullscreen={setFullscreenIdx}
            onPageKindChange={setPageKind}
            onViewModeChange={setViewMode}
            pageKind={pageKind}
            view={familyView}
            viewMode={viewMode}
          />

          <div className="gallery-detail-stats">
            <span>조회 {item.view_count}</span>
            <span>활용 {item.use_count}</span>
            <span>다운 {item.download_count || 0}</span>
            <span>좋아요 {item.like_count}</span>
          </div>

          {familyView.isFamily && familyHasVersion(family, 'image') && (
            <p className="gallery-detail-hydrate-hint">
              이미지형은 여기서 받을 수 있고, 가져간 뒤에는 내용을 고쳐 새로 만들 수 있어요
            </p>
          )}

          <ModalActions primary={primaryAction} testId="gallery-detail-actions" />
          <div className="gallery-detail-sub-actions">
            <button type="button" className={`gallery-detail-text-btn ${item.is_liked ? 'gallery-detail-text-btn--liked' : ''}`} onClick={() => onLike(item)} aria-pressed={Boolean(item.is_liked)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill={item.is_liked ? '#EF4444' : 'none'} stroke={item.is_liked ? '#EF4444' : '#6B7280'} strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              좋아요
            </button>
            <button type="button" className={`gallery-detail-text-btn ${item.is_favorited ? 'gallery-detail-text-btn--fav' : ''}`} onClick={() => onFavorite(item)} aria-pressed={Boolean(item.is_favorited)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill={item.is_favorited ? '#3182F6' : 'none'} stroke={item.is_favorited ? '#3182F6' : '#6B7280'} strokeWidth="2">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
              즐겨찾기
            </button>
            {familyView.isFamily ? (
              <GalleryFamilyActions
                item={item}
                onOverlayChange={setFamilyOverlay}
                onTrackDownload={onTrackDownload}
                outline={familyView.outline}
                viewMode={viewMode}
              />
            ) : fileUrl && item.content_type !== 'other' ? (
              <button
                type="button"
                className="gallery-detail-text-btn"
                disabled={downloadBusy}
                onClick={() => onDownload(fileUrl, item.id, item.content_type, item.title)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {downloadBusy ? (downloadLabel || '파일 준비 중…') : '다운로드'}
              </button>
            ) : null}
            <button type="button" className="gallery-detail-text-btn" onClick={() => onShare(item)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
              공유
            </button>
          </div>

          {item.can_delete && (
            <button type="button" className="gallery-detail-delete-btn" onClick={() => onDelete(item)}>
              {item.is_mine ? '내가 공유한 자료 삭제하기' : '나눔터에서 내리기'}
            </button>
          )}
        </div>
        </>
        )}
      </Dialog>

      {fullscreenIdx !== null && familyView.fullscreenSlides.length > 0 && (
        <HtmlSlideFullscreen
          slides={familyView.fullscreenSlides}
          initialIndex={fullscreenIdx}
          onClose={() => setFullscreenIdx(null)}
          hideEditButton
          hidePresentButton
          themeId={familyView.fullscreenThemeId}
        />
      )}
    </>
  );
};

const GalleryPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const openConfirm = useConfirm();
  const {
    download: downloadLibraryFile,
    isDownloading: isLibraryDownloading,
    buttonLabel: libraryDownloadLabel,
  } = useFileDownload();
  const [searchParams, setSearchParams] = useSearchParams();
  const { itemId: urlItemId } = useParams();

  const [initialParams] = useState(() => parseLibraryParams(searchParams));
  const [mainTab, setMainTab] = useState(initialParams.mainTab);
  const [typeFilter, setTypeFilter] = useState(initialParams.typeFilter);
  const [schoolLevel, setSchoolLevel] = useState(initialParams.schoolLevel);
  const [subject, setSubject] = useState(initialParams.subject);
  const [sort, setSort] = useState(initialParams.sort);
  const [lists, setLists] = useState(emptyLibraryLists);
  const [loading, setLoading] = useState(true);
  const [loadErrorKey, setLoadErrorKey] = useState(null);
  const requestSeqRef = useRef(0);
  const [detailItem, setDetailItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPreviewId, setDetailPreviewId] = useState(null);
  const [searchQuery, setSearchQuery] = useState(initialParams.search);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(initialParams.search);
  const [myContentDetail, setMyContentDetail] = useState(null);
  const [authUser, setAuthUser] = useState(() => auth.currentUser);
  const [authReady, setAuthReady] = useState(() => (
    Boolean(auth.currentUser) || typeof auth.onAuthStateChanged !== 'function'
  ));
  const debounceRef = useRef(null);

  const firstAuthStateRef = useRef(true);
  useEffect(() => {
    if (typeof auth.onAuthStateChanged !== 'function') return undefined;
    return auth.onAuthStateChanged((user) => {
      // 주소에 탭이 없으면 로그인 복원 결과로 기본 탭을 한 번만 정한다(로그인하면 내 자료, 아니면 나눔터).
      if (firstAuthStateRef.current && !searchParamsRef.current.get('tab')) {
        setMainTab(user ? 'my' : 'nanumteo');
      }
      firstAuthStateRef.current = false;
      setAuthUser(user);
      setAuthReady(true);
    });
  }, []);
  const requestShareMyContent = useCallback(async (myItem) => {
    const result = await shareToGallery({
      content_type: myItem.content_type,
      title: myItem.title,
      thumbnail_url: myItem.thumbnail_url || null,
      download_url: myItem.download_url || null,
      source_data: buildLibraryShareSourceData(myItem),
      tags: [],
    });
    setLists((prev) => ({
      ...prev,
      my: patchLibraryListItem(prev.my, myItem.id, { is_shared: true, shared_gallery_id: result.id }),
    }));
    setMyContentDetail((prev) => (
      prev?.id === myItem.id
        ? { ...prev, is_shared: true, shared_gallery_id: result.id }
        : prev
    ));
    return result;
  }, []);
  const { share: shareMyContent, isSharing: isSharingMyContent } = useGalleryShare({
    requestShare: requestShareMyContent,
    showToast,
    onView: (galleryId) => navigate(gallerySharePath(galleryId)),
  });

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!searchQuery.trim()) {
      setDebouncedSearch('');
      return;
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  // 라우터는 주소 변경을 transition으로 한 박자 늦게 반영한다. 그사이 옛 주소를 기준으로 고치면
  // 방금 바꾼 값이 되돌아가므로(탭을 바꾸자마자 q만 지우면 탭이 나눔터로 돌아감), 마지막으로 쓴 주소를 기준으로 삼는다.
  const searchParamsRef = useRef(searchParams);
  const routerParamsRef = useRef(searchParams);
  if (routerParamsRef.current !== searchParams) {
    routerParamsRef.current = searchParams;
    searchParamsRef.current = searchParams;
  }
  const setSearchParamsRef = useRef(setSearchParams);
  const debouncedSearchRef = useRef(debouncedSearch);
  const myContentDetailRef = useRef(myContentDetail);
  setSearchParamsRef.current = setSearchParams;
  debouncedSearchRef.current = debouncedSearch;
  myContentDetailRef.current = myContentDetail;

  // 목록에서 연 상세는 history에 한 칸을 쌓는다. 닫을 때 그 칸을 되돌려야 뒤로가기와 닫기가 같은 동작이 된다.
  const openedDetailFromListRef = useRef(false);
  const openedMineFromListRef = useRef(false);

  const writeParams = useCallback((params, options) => {
    searchParamsRef.current = params;
    setSearchParamsRef.current(params, options);
  }, []);

  const replaceParams = useCallback((mutate) => {
    const params = new URLSearchParams(searchParamsRef.current);
    mutate(params);
    writeParams(params, { replace: true });
  }, [writeParams]);

  useEffect(() => {
    const current = searchParamsRef.current.get('q') || '';
    if (current === debouncedSearch) return;
    replaceParams((params) => {
      if (debouncedSearch) params.set('q', debouncedSearch);
      else params.delete('q');
    });
  }, [debouncedSearch, replaceParams]);

  const openMyContentById = useCallback(async (mineId) => {
    try {
      if (auth.authStateReady) await auth.authStateReady();
      const full = await getMyContentItem(mineId);
      if (searchParamsRef.current.get('mine') !== mineId) return;
      if (full?.id) setMyContentDetail(full);
      else throw new Error('not found');
    } catch (err) {
      console.error('My content item load error:', err);
      if (searchParamsRef.current.get('mine') === mineId) {
        replaceParams((params) => params.delete('mine'));
      }
    }
  }, [replaceParams]);

  useEffect(() => {
    if (initialParams.mineId) openMyContentById(initialParams.mineId);
  }, [initialParams.mineId, openMyContentById]);

  const syncedSearchKeyRef = useRef(searchParams.toString());
  useEffect(() => {
    const searchKey = searchParams.toString();
    if (syncedSearchKeyRef.current === searchKey) return;
    syncedSearchKeyRef.current = searchKey;
    const next = parseLibraryParams(searchParams);
    setMainTab(next.mainTab);
    setTypeFilter(next.typeFilter);
    setSchoolLevel(next.schoolLevel);
    setSubject(next.subject);
    setSort(next.sort);
    if (next.search !== debouncedSearchRef.current) {
      setSearchQuery(next.search);
      setDebouncedSearch(next.search);
    }
    if (!next.mineId) {
      openedMineFromListRef.current = false;
      setMyContentDetail(null);
    } else if (myContentDetailRef.current?.id !== next.mineId) {
      openMyContentById(next.mineId);
    }
  }, [searchParams, openMyContentById]);

  const handleMyContentDetailLoaded = useCallback((full) => {
    setLists((prev) => ({
      ...prev,
      my: patchLibraryListItem(prev.my, full.id, (it) => ({
        ...full,
        is_shared: Boolean(it.is_shared || full.is_shared),
        shared_gallery_id: it.shared_gallery_id || full.shared_gallery_id || null,
      })),
    }));
  }, []);

  const listKey = buildLibraryListKey({
    tab: mainTab,
    typeFilter,
    schoolLevel,
    subject,
    sort,
    search: debouncedSearch,
    viewer: authUser?.uid || '',
  });
  const needsLogin = authReady && !authUser && mainTab !== 'nanumteo';

  const requestListPage = useCallback((tab, pageNum) => {
    const contentType = typeFilter === 'all' ? undefined : typeFilter;
    if (tab === 'my') {
      return getMyContent({ contentType, page: pageNum, limit: LIBRARY_LIST_PAGE_SIZE });
    }
    return getGalleryItems({
      contentType,
      schoolLevel: schoolLevel === 'all' ? undefined : schoolLevel,
      subject: subject === 'all' ? undefined : subject,
      sort,
      page: pageNum,
      limit: LIBRARY_LIST_PAGE_SIZE,
      search: debouncedSearch || undefined,
      favoritesOnly: tab === 'favorites',
    });
  }, [typeFilter, schoolLevel, subject, sort, debouncedSearch]);

  // 마지막 요청의 응답만 반영한다. 정렬·필터를 빠르게 바꿀 때 늦게 온 옛 응답이 새 목록을 덮지 않게 한다.
  const loadListPage = useCallback(async (pageNum) => {
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    const tab = mainTab;
    const key = listKey;
    setLoading(true);
    setLoadErrorKey(null);
    try {
      const data = await requestListPage(tab, pageNum);
      if (seq !== requestSeqRef.current) return;
      setLists((prev) => ({
        ...prev,
        [tab]: applyLibraryListPage(prev[tab], {
          key,
          pageNum,
          items: data?.items || [],
          hasNext: data?.has_next,
          totalCount: data?.total_count,
          sortItems: tab === 'my' ? sortLibraryItemsByUpdatedAt : null,
        }),
      }));
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      console.error('Library fetch error:', err);
      setLoadErrorKey(key);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [mainTab, listKey, requestListPage]);

  const loadListPageRef = useRef(loadListPage);
  loadListPageRef.current = loadListPage;

  // 새로고침 직후에는 로그인 복원이 끝나기 전이라 토큰 없이 부르면 내 자료가 실패하고 좋아요 표시가 빠진다.
  useEffect(() => {
    if (!authReady || needsLogin) return;
    loadListPageRef.current(1);
  }, [listKey, authReady, needsLogin]);

  // 채팅에서 자료를 고치고 돌아왔을 때 내 자료만 1쪽을 다시 받아 합친다.
  // 불러온 뒤쪽 카드와 쪽 번호는 그대로 두어 스크롤이 튀거나 '더 보기'가 쪽을 건너뛰지 않게 한다.
  const lastRefreshAtRef = useRef(0);
  useEffect(() => {
    if (mainTab !== 'my') return undefined;
    const refresh = async () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastRefreshAtRef.current < 1000) return;
      lastRefreshAtRef.current = now;
      const key = listKey;
      try {
        const data = await requestListPage('my', 1);
        setLists((prev) => (
          prev.my.key !== key
            ? prev
            : {
              ...prev,
              my: refreshLibraryListHead(prev.my, {
                items: data?.items || [],
                hasNext: data?.has_next,
                sortItems: sortLibraryItemsByUpdatedAt,
              }),
            }
        ));
      } catch (err) {
        console.error('My content refresh error:', err);
      }
    };
    const onVisibility = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', refresh);
    };
  }, [mainTab, listKey, requestListPage]);

  const detailItemRef = useRef(detailItem);
  detailItemRef.current = detailItem;
  const pendingDetailIdRef = useRef(null);

  useEffect(() => {
    if (!urlItemId) {
      // 뒤로가기로 /library 로 돌아오면 상세도 닫는다.
      openedDetailFromListRef.current = false;
      pendingDetailIdRef.current = null;
      setDetailItem(null);
      setDetailPreviewId(null);
      return;
    }
    if (pendingDetailIdRef.current === urlItemId || detailItemRef.current?.id === urlItemId) return;
    pendingDetailIdRef.current = urlItemId;
    (async () => {
      setDetailLoading(true);
      try {
        if (auth.authStateReady) await auth.authStateReady();
        const detail = await getGalleryItem(urlItemId);
        if (pendingDetailIdRef.current === urlItemId) setDetailItem(detail);
      } catch (err) {
        if (pendingDetailIdRef.current !== urlItemId) return;
        pendingDetailIdRef.current = null;
        // 공유 링크로 들어왔는데 자료가 없거나(삭제·비공개) 서버 오류면 조용히 목록만 보이던 문제.
        console.error('URL item load error:', err);
        const status = err?.status;
        showToast(
          status === 404 || status === 403
            ? '이 자료는 삭제되었거나 더 이상 공개되지 않아요'
            : '자료를 불러오지 못했어요. 잠시 후 다시 시도해주세요',
          'error',
        );
        navigate('/library?tab=nanumteo', { replace: true });
      } finally {
        setDetailLoading(false);
      }
    })();
    // showToast·navigate 는 안정적인 핸들러라 의존성에 넣지 않는다(넣으면 위치가 바뀔 때마다 다시 불러온다).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlItemId]);

  const handleMainTabChange = (tabId) => {
    if ((tabId === 'my' || tabId === 'favorites') && !auth.currentUser) {
      setShowLoginPrompt(true);
      return;
    }
    setMainTab(tabId);
    setSearchQuery('');
    setDebouncedSearch('');
    const params = new URLSearchParams(searchParamsRef.current);
    params.set('tab', tabId);
    params.delete('q');
    writeParams(params, { replace: true });
  };

  const handleTypeFilterChange = (filterId) => {
    setTypeFilter(filterId);
    const params = new URLSearchParams(searchParamsRef.current);
    if (filterId === 'all') params.delete('type');
    else params.set('type', filterId);
    writeParams(params, { replace: true });
  };

  const handleSchoolLevelChange = (schoolId) => {
    const nextSubjectOptions = getLibrarySubjectFilters(schoolId);
    const subjectStillAvailable = nextSubjectOptions.some(({ id }) => id === subject);
    const nextSubject = subjectStillAvailable ? subject : 'all';
    setSchoolLevel(schoolId);
    setSubject(nextSubject);
    const params = new URLSearchParams(searchParamsRef.current);
    if (schoolId === 'all') params.delete('school');
    else params.set('school', schoolId);
    if (nextSubject === 'all') params.delete('subject');
    else params.set('subject', nextSubject);
    writeParams(params, { replace: true });
  };

  const handleSubjectChange = (subjectId) => {
    setSubject(subjectId);
    const params = new URLSearchParams(searchParamsRef.current);
    if (schoolLevel === 'all') params.delete('school');
    else params.set('school', schoolLevel);
    if (subjectId === 'all') params.delete('subject');
    else params.set('subject', subjectId);
    writeParams(params, { replace: true });
  };

  const resetListFilters = () => {
    setTypeFilter('all');
    setSchoolLevel('all');
    setSubject('all');
    const params = new URLSearchParams(searchParamsRef.current);
    ['type', 'school', 'subject'].forEach((key) => params.delete(key));
    writeParams(params, { replace: true });
  };

  const resetTaxonomyFilters = () => {
    setSchoolLevel('all');
    setSubject('all');
    const params = new URLSearchParams(searchParamsRef.current);
    params.delete('school');
    params.delete('subject');
    writeParams(params, { replace: true });
  };

  const handleSortChange = (sortId) => {
    setSort(sortId);
    const params = new URLSearchParams(searchParamsRef.current);
    if (sortId === 'latest') params.delete('sort');
    else params.set('sort', sortId);
    writeParams(params, { replace: true });
  };

  const handleLoadMore = () => {
    if (loading) return;
    const current = lists[mainTab];
    const nextPage = current.key === listKey ? current.page + 1 : 1;
    loadListPage(nextPage);
  };

  const patchGalleryLists = (itemId, patch) => {
    setLists((prev) => ({
      ...prev,
      nanumteo: patchLibraryListItem(prev.nanumteo, itemId, patch),
      favorites: patchLibraryListItem(prev.favorites, itemId, patch),
    }));
  };

  const updateItem = (itemId, updates) => {
    patchGalleryLists(itemId, updates);
    setDetailItem((prev) => (prev?.id === itemId ? { ...prev, ...updates } : prev));
  };

  // 서버가 "뒤집기"로 동작해서, 응답 전에 한 번 더 누르면 원래대로 돌아간다. 요청 중에는 같은 버튼을 무시한다.
  const toggleInFlightRef = useRef(new Set());

  const runOptimisticToggle = async ({ key, item, optimistic, rollback, request, apply, failMessage }) => {
    if (!auth.currentUser) { setShowLoginPrompt(true); return; }
    if (toggleInFlightRef.current.has(key)) return;
    toggleInFlightRef.current.add(key);
    updateItem(item.id, optimistic);
    try {
      const result = await request(item.id);
      updateItem(item.id, apply(result));
      return result;
    } catch (err) {
      updateItem(item.id, rollback);
      showToast(failMessage, 'error');
      return undefined;
    } finally {
      toggleInFlightRef.current.delete(key);
    }
  };

  const handleLike = (item) => {
    const liked = Boolean(item.is_liked);
    const count = Number(item.like_count) || 0;
    return runOptimisticToggle({
      key: `like:${item.id}`,
      item,
      optimistic: { is_liked: !liked, like_count: Math.max(0, count + (liked ? -1 : 1)) },
      rollback: { is_liked: liked, like_count: count },
      request: toggleGalleryLike,
      apply: (result) => ({ is_liked: result.is_liked, like_count: result.like_count }),
      failMessage: '좋아요를 반영하지 못했어요. 다시 눌러주세요',
    });
  };

  const handleFavorite = async (item) => {
    const favorited = Boolean(item.is_favorited);
    const result = await runOptimisticToggle({
      key: `favorite:${item.id}`,
      item,
      optimistic: { is_favorited: !favorited },
      rollback: { is_favorited: favorited },
      request: toggleGalleryFavorite,
      apply: (response) => ({ is_favorited: response.is_favorited }),
      failMessage: '즐겨찾기를 반영하지 못했어요. 다시 눌러주세요',
    });
    if (result?.is_favorited) showToast('즐겨찾기에 담았어요', 'success');
  };

  const libraryQuery = () => {
    const query = searchParamsRef.current.toString();
    return query ? `?${query}` : '';
  };

  const closeDetail = () => {
    pendingDetailIdRef.current = null;
    setDetailItem(null);
    setDetailPreviewId(null);
    if (!urlItemId) return;
    if (openedDetailFromListRef.current) {
      openedDetailFromListRef.current = false;
      navigate(-1);
    } else {
      navigate(`/library${libraryQuery()}`, { replace: true });
    }
  };

  const handleCardClick = async (item) => {
    pendingDetailIdRef.current = item.id;
    if (urlItemId !== item.id) {
      openedDetailFromListRef.current = true;
      navigate(`/library/${item.id}${libraryQuery()}`);
    }
    setDetailItem(item);
    setDetailPreviewId(item.id);
    try {
      const detail = await getGalleryItem(item.id);
      if (pendingDetailIdRef.current !== item.id) return;
      pendingDetailIdRef.current = null;
      setDetailItem(detail);
      setDetailPreviewId(null);
    } catch (err) {
      console.error('Detail fetch error:', err);
      if (pendingDetailIdRef.current === item.id) {
        showToast('자료를 불러오지 못했어요. 잠시 후 다시 시도해주세요', 'error');
        closeDetail();
      }
    }
  };

  const openMyContentDetail = (item) => {
    setMyContentDetail(item);
    openedMineFromListRef.current = true;
    const params = new URLSearchParams(searchParamsRef.current);
    params.set('mine', item.id);
    writeParams(params);
  };

  const closeMyContentDetail = () => {
    setMyContentDetail(null);
    if (!searchParamsRef.current.get('mine')) return;
    if (openedMineFromListRef.current) {
      openedMineFromListRef.current = false;
      navigate(-1);
    } else {
      replaceParams((params) => params.delete('mine'));
    }
  };

  const handleDelete = async (itemOrId) => {
    const targetItem = typeof itemOrId === 'string' ? detailItem : itemOrId;
    const itemId = typeof itemOrId === 'string' ? itemOrId : itemOrId?.id;
    if (!itemId) return;
    const ok = await openConfirm(targetItem?.is_mine
      ? {
          title: '이 자료를 삭제할까요?',
          description: '삭제한 자료는 되돌릴 수 없어요',
          confirmLabel: '삭제하기',
          danger: true,
        }
      : {
          title: '나눔터에서 이 자료를 내릴까요?',
          description: '내린 자료는 나눔터에서 더 볼 수 없어요',
          confirmLabel: '내리기',
          danger: true,
        });
    if (!ok) return;
    try {
      await deleteGalleryItem(itemId);
      setLists((prev) => ({
        ...prev,
        nanumteo: removeLibraryListItem(prev.nanumteo, itemId),
        favorites: removeLibraryListItem(prev.favorites, itemId),
      }));
      closeDetail();
    } catch (err) {
      showToast('삭제하지 못했어요. 잠시 후 다시 시도해주세요', 'error');
    }
  };

  const handleShare = async (item) => {
    const url = `${window.location.origin}/library/${item.id}`;
    const referralShare = await prepareTeacherReferralShareUrl({
      user: auth.currentUser,
      url,
      surface: 'library',
      audience: 'teacher',
    });
    const result = await shareContent({
      title: item.title,
      text: '왓퀴즈 나눔터에서 이 수업 자료를 확인해보세요.',
      url: referralShare.url,
    });
    if (result.success) {
      trackInviteShare(
        'library',
        result.method,
        referralShare.referralAttached,
      );
      showToast(
        result.method === 'clipboard'
          ? '링크를 복사했어요'
          : '공유 화면을 열었어요',
        'success',
      );
    } else if (result.method !== 'cancelled') {
      showToast('공유하지 못했어요. 다시 시도해주세요.', 'error');
    }
  };

  const handleShareMyContent = async (myItem, silent = false) => (
    shareMyContent(myItem, {
      silent,
      confirm: silent
        ? undefined
        : (item) => openConfirm(item.content_type === 'other'
          ? {
              title: '나눔터에 공개할까요?',
              description: `${item.title}\n공유 후에는 누구나 다운로드할 수 있어요.\n민감한 정보가 없는지 먼저 확인해주세요`,
              confirmLabel: '공유하기',
            }
          : {
              title: '나눔터에 공유할까요?',
              description: `${item.title}\n다른 선생님이 이 자료를 참고해서 활용할 수 있어요`,
              confirmLabel: '공유하기',
            }),
    })
  );

  const handleUse = async (item) => {
    if (!auth.currentUser) { setShowLoginPrompt(true); return; }
    try { await markGalleryItemUsed(item.id); } catch (_) {}

    const categoryMap = { ppt: 'ppt_maker', worksheet: 'worksheet_gen', image: 'image_generation' };
    navigate('/chat', {
      state: {
        category: categoryMap[item.content_type] || 'ppt_maker',
        galleryReference: {
          galleryItemId: item.id,
          title: item.title,
          sourceData: item.source_data || {},
          contentType: item.content_type,
          thumbnailUrl: item.thumbnail_url || null,
        },
      },
    });
  };

  const currentList = lists[mainTab];
  const isListFresh = currentList.key === listKey;
  const displayItems = isListFresh ? currentList.items : [];
  const hasNext = isListFresh && currentList.hasNext;
  const listFailed = loadErrorKey === listKey;
  const showSkeleton = !listFailed && (!isListFresh || (loading && displayItems.length === 0));
  const showTaxonomyFilters = mainTab !== 'my';
  const activeTaxonomyCount = (schoolLevel !== 'all' ? 1 : 0) + (subject !== 'all' ? 1 : 0);
  const listTotalCount = isListFresh ? currentList.totalCount : 0;
  const listCountText = !showSkeleton && listTotalCount > 0
    ? `자료 ${listTotalCount.toLocaleString('ko-KR')}개`
    : '';
  const hasListFilters = typeFilter !== 'all' || (showTaxonomyFilters && activeTaxonomyCount > 0);
  // 비어 있는 이유마다 바로 할 수 있는 한 가지 행동을 준다.
  const emptyState = (() => {
    if (mainTab === 'nanumteo' && debouncedSearch) {
      return {
        title: `'${debouncedSearch}' 검색 결과가 없어요`,
        description: hasListFilters ? '검색어나 필터를 바꿔서 다시 찾아보세요' : '다른 키워드로 찾아보세요',
        actionLabel: '검색어 지우기',
        onAction: () => setSearchQuery(''),
      };
    }
    if (mainTab === 'my' && typeFilter !== 'all') {
      const typeLabel = CONTENT_TYPE_FILTERS.find(({ id }) => id === typeFilter)?.label || '';
      return {
        title: `아직 ${typeLabel} 자료가 없어요`,
        description: typeFilter === 'other'
          ? '다운로드한 문서 파일이 여기에 모여요'
          : `AI로 만든 ${typeLabel} 자료가 여기에 모여요`,
        actionLabel: '전체 자료 보기',
        onAction: resetListFilters,
      };
    }
    if (hasListFilters) {
      return {
        title: '조건에 맞는 자료가 없어요',
        description: '필터를 줄이면 더 많은 자료를 볼 수 있어요',
        actionLabel: '필터 모두 지우기',
        onAction: resetListFilters,
      };
    }
    if (mainTab === 'favorites') {
      return {
        title: '즐겨찾기한 자료가 없어요',
        description: '나눔터에서 마음에 드는 자료의 북마크를 눌러 모아보세요',
        actionLabel: '나눔터 둘러보기',
        onAction: () => handleMainTabChange('nanumteo'),
      };
    }
    return {
      title: mainTab === 'my' ? '아직 만든 자료가 없어요' : '아직 공유된 자료가 없어요',
      description: mainTab === 'my'
        ? 'AI로 만든 PPT, 학습지, 이미지와 다운로드한 파일이 여기에 모여요'
        : '선생님들이 만든 자료가 곧 올라올 거예요',
      actionLabel: '자료 만들러 가기',
      onAction: () => navigate('/chat'),
    };
  })();
  const loadingMore = loading && isListFresh && displayItems.length > 0;
  const { sentinelRef, autoLoadExhausted } = useAutoLoadMore({
    enabled: hasNext && !loading && !listFailed && !needsLogin && displayItems.length > 0,
    listKey,
    page: isListFresh ? currentList.page : 0,
    onLoadMore: handleLoadMore,
  });
  const showListHeader = !needsLogin
    && !listFailed
    && (showSkeleton || displayItems.length > 0)
    && (showTaxonomyFilters || listCountText);

  return (
    <>
      <Helmet>
        <title>라이브러리 | 교사 자료 공유 - 왓퀴즈</title>
        <meta name="description" content="선생님들이 AI로 만든 학습지, PPT, 이미지와 문서 파일을 관리하고 공유하세요. 내가 만든 자료를 한눈에 보고, 다른 선생님의 자료도 활용해보세요." />
        <meta name="keywords" content="교사 자료 공유, 학습지 공유, PPT 공유, 교육용 이미지, 수업 자료, AI 학습지, AI PPT, 교사 커뮤니티, 왓퀴즈" />
        <meta property="og:title" content="라이브러리 | 교사 자료 공유 - 왓퀴즈" />
        <meta property="og:description" content="선생님들이 AI로 만든 학습지, PPT, 교육용 이미지를 관리하고 공유하세요." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://whatquiz.co.kr/library" />
        <link rel="canonical" href="https://whatquiz.co.kr/library" />
        {detailItem && (
          <>
            <title>{detailItem.title} | 라이브러리 - 왓퀴즈</title>
            <meta name="description" content={`${detailItem.title} - 선생님이 공유한 ${detailItem.content_type === 'ppt' ? 'PPT' : detailItem.content_type === 'worksheet' ? '학습지' : detailItem.content_type === 'other' ? '문서 파일' : '이미지'} 자료. 왓퀴즈 라이브러리에서 확인해보세요.`} />
            <meta property="og:title" content={`${detailItem.title} | 라이브러리 - 왓퀴즈`} />
            <meta property="og:description" content={`${detailItem.title} - 선생님이 공유한 교육 자료`} />
            {detailItem.thumbnail_url && <meta property="og:image" content={detailItem.thumbnail_url} />}
            <meta property="og:url" content={`https://whatquiz.co.kr/library/${detailItem.id}`} />
            <link rel="canonical" href={`https://whatquiz.co.kr/library/${detailItem.id}`} />
            <script type="application/ld+json">{JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CreativeWork",
              "name": detailItem.title,
              "description": detailItem.description || `${detailItem.title} - 교사가 AI로 만든 교육 자료`,
              "url": `https://whatquiz.co.kr/library/${detailItem.id}`,
              "image": detailItem.thumbnail_url || undefined,
              "educationalUse": "교육",
              "inLanguage": "ko",
              "provider": { "@type": "Organization", "name": "왓퀴즈", "url": "https://whatquiz.co.kr" },
              "interactionStatistic": [
                { "@type": "InteractionCounter", "interactionType": "https://schema.org/ViewAction", "userInteractionCount": detailItem.view_count },
                { "@type": "InteractionCounter", "interactionType": "https://schema.org/LikeAction", "userInteractionCount": detailItem.like_count },
              ],
            })}</script>
          </>
        )}
      </Helmet>

      <div className="gallery-page">
        <div className="gallery-header">
          <h1 className="gallery-page-title">라이브러리</h1>
          <p className="gallery-page-subtitle">내가 만든 자료를 관리하고, 다른 선생님의 자료도 참고하세요</p>
        </div>

        {/* 메인 탭: 내 자료 / 나눔터 / 즐겨찾기 */}
        <div className="gallery-controls">
          <div className="gallery-tabs">
            {MAIN_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`gallery-tab ${mainTab === tab.id ? 'gallery-tab--active' : ''}`}
                aria-pressed={mainTab === tab.id}
                onClick={() => handleMainTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {!needsLogin && (
        <section className="gallery-toolbar" aria-label="자료 필터">
          {mainTab === 'nanumteo' && (
            <div className="gallery-search">
              <svg className="gallery-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                id="gallery-search-input"
                className="gallery-search-input"
                type="search"
                enterKeyHint="search"
                aria-label="자료 검색"
                placeholder="자료 검색 (예: 3학년 과학, 아이스브레이킹)"
                maxLength={MAX_SEARCH_LENGTH}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button type="button" className="gallery-search-clear" onClick={() => setSearchQuery('')} aria-label="검색어 지우기">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              )}
            </div>
          )}

          <div className="gallery-toolbar-row">
            <div className="gallery-filter-chips" role="group" aria-label="자료 유형">
              {CONTENT_TYPE_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`gallery-filter-chip ${typeFilter === f.id ? 'gallery-filter-chip--active' : ''}`}
                  onClick={() => handleTypeFilterChange(f.id)}
                  aria-pressed={typeFilter === f.id}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {showTaxonomyFilters && (
              <button
                type="button"
                className={`gallery-filter-button ${activeTaxonomyCount ? 'gallery-filter-button--active' : ''}`}
                onClick={() => setFilterSheetOpen(true)}
                aria-haspopup="dialog"
                aria-label={activeTaxonomyCount ? `학교급·교과 필터, ${activeTaxonomyCount}개 적용됨` : '학교급·교과 필터'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <line x1="4" y1="7" x2="20" y2="7" /><line x1="7" y1="12" x2="17" y2="12" /><line x1="10" y1="17" x2="14" y2="17" />
                </svg>
                필터
                {activeTaxonomyCount > 0 && <span className="gallery-filter-button-count" aria-hidden="true">{activeTaxonomyCount}</span>}
              </button>
            )}
          </div>

          {showTaxonomyFilters && activeTaxonomyCount > 0 && (
            <div className="gallery-applied-filters" data-testid="gallery-applied-filters">
              {schoolLevel !== 'all' && (
                <button type="button" className="gallery-applied-chip" onClick={() => handleSchoolLevelChange('all')} aria-label={`${getLibrarySchoolLabel(schoolLevel)} 필터 빼기`}>
                  {getLibrarySchoolLabel(schoolLevel)}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              )}
              {subject !== 'all' && (
                <button type="button" className="gallery-applied-chip" onClick={() => handleSubjectChange('all')} aria-label={`${getLibrarySubjectLabel(subject)} 필터 빼기`}>
                  {getLibrarySubjectLabel(subject)}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              )}
              {activeTaxonomyCount > 1 && (
                <button type="button" className="gallery-applied-reset" onClick={resetTaxonomyFilters}>
                  모두 지우기
                </button>
              )}
            </div>
          )}
        </section>
        )}

        {showListHeader && (
          <div className="gallery-list-header">
            <p className="gallery-list-count" aria-live="polite">
              {listCountText}
            </p>
            {showTaxonomyFilters && (
              <div className="gallery-sort-select-wrap">
                <select
                  className="gallery-sort-select"
                  aria-label="정렬"
                  value={sort}
                  onChange={(e) => handleSortChange(e.target.value)}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            )}
          </div>
        )}

        {needsLogin ? (
          <div className="gallery-empty" data-testid="gallery-login-needed">
            <p className="gallery-empty-title">
              {mainTab === 'favorites' ? '로그인하면 즐겨찾기한 자료를 모아 볼 수 있어요' : '로그인하면 만든 자료가 여기에 모여요'}
            </p>
            <p className="gallery-empty-desc">
              {mainTab === 'favorites'
                ? '나눔터에서 마음에 드는 자료를 담아 두고 수업 때 바로 꺼내 써요'
                : 'AI로 만든 PPT·학습지·이미지가 자동으로 저장돼요'}
            </p>
            <button type="button" className="gallery-empty-cta" onClick={() => setShowLoginPrompt(true)}>
              로그인하기
            </button>
          </div>
        ) : showSkeleton ? (
          <div className="gallery-grid" aria-busy="true" aria-label="자료를 불러오는 중" data-testid="gallery-list-skeleton">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="gallery-card gallery-card--skeleton">
                <div className="gallery-card-thumb gallery-skeleton-shimmer" />
                <div className="gallery-card-body">
                  <div className="gallery-skeleton-line gallery-skeleton-shimmer" style={{ width: '70%' }} />
                  <div className="gallery-skeleton-line gallery-skeleton-shimmer" style={{ width: '40%', marginTop: 8 }} />
                </div>
              </div>
            ))}
          </div>
        ) : listFailed && displayItems.length === 0 ? (
          <div className="gallery-empty" role="alert" data-testid="gallery-load-error">
            <p className="gallery-empty-title">자료를 불러오지 못했어요</p>
            <p className="gallery-empty-desc">인터넷 연결을 확인하고 다시 불러와 주세요</p>
            <button type="button" className="gallery-empty-cta" onClick={() => loadListPage(1)}>
              다시 불러오기
            </button>
          </div>
        ) : displayItems.length === 0 ? (
          <div className="gallery-empty">
            <div className="gallery-empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5">
                {mainTab === 'favorites' ? (
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                ) : mainTab === 'my' ? (
                  <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></>
                ) : (
                  <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/></>
                )}
              </svg>
            </div>
            <p className="gallery-empty-title">{emptyState.title}</p>
            <p className="gallery-empty-desc">{emptyState.description}</p>
            <button type="button" className="gallery-empty-cta" onClick={emptyState.onAction}>
              {emptyState.actionLabel}
            </button>
          </div>
        ) : (
          <>
            <div className="gallery-grid">
              {mainTab === 'my' ? (
                displayItems.map((item) => (
                  <MyContentCard
                    key={item.id}
                    item={item}
                    onClick={openMyContentDetail}
                  />
                ))
              ) : (
                displayItems.map((item) => (
                  <GalleryCard key={item.id} item={item} onLike={handleLike} onFavorite={handleFavorite} onClick={handleCardClick} />
                ))
              )}
            </div>
            {listFailed ? (
              <div className="gallery-load-more" role="alert">
                <p className="gallery-load-more-error">자료를 더 불러오지 못했어요</p>
                <button type="button" className="gallery-load-more-btn" onClick={handleLoadMore} disabled={loading}>
                  다시 불러오기
                </button>
              </div>
            ) : loadingMore ? (
              <div className="gallery-grid gallery-grid--more" aria-busy="true" aria-label="자료를 더 불러오는 중" data-testid="gallery-load-more-skeleton">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="gallery-card gallery-card--skeleton">
                    <div className="gallery-card-thumb gallery-skeleton-shimmer" />
                    <div className="gallery-card-body">
                      <div className="gallery-skeleton-line gallery-skeleton-shimmer" style={{ width: '70%' }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : hasNext && (
              autoLoadExhausted ? (
                <div className="gallery-load-more">
                  <button type="button" className="gallery-load-more-btn" onClick={handleLoadMore}>
                    더 보기
                  </button>
                </div>
              ) : (
                <div ref={sentinelRef} className="gallery-load-more-sentinel" aria-hidden="true" data-testid="gallery-load-more-sentinel" />
              )
            )}
          </>
        )}
      </div>

      {/* 고르는 즉시 목록에 반영한다. 닫기 버튼은 "적용"이 아니라 결과를 보러 가는 동작이다 */}
      <Dialog
        open={filterSheetOpen && showTaxonomyFilters}
        onClose={() => setFilterSheetOpen(false)}
        title="필터"
        size="sm"
        testId="gallery-filter-sheet"
        footer={(
          <ModalActions
            primary={{ label: '결과 보기', onClick: () => setFilterSheetOpen(false) }}
            secondary={activeTaxonomyCount > 0 ? { label: '초기화', onClick: resetTaxonomyFilters } : undefined}
          />
        )}
      >
        <div className="gallery-filter-sheet">
          <div className="gallery-filter-sheet-group">
            <span className="gallery-filter-sheet-label" id="gallery-school-label">학교급</span>
            <div className="gallery-filter-sheet-chips" role="group" aria-labelledby="gallery-school-label">
              {LIBRARY_SCHOOL_LEVELS.map((level) => (
                <button
                  key={level.id}
                  type="button"
                  className={`gallery-filter-chip ${schoolLevel === level.id ? 'gallery-filter-chip--active' : ''}`}
                  onClick={() => handleSchoolLevelChange(level.id)}
                  aria-pressed={schoolLevel === level.id}
                >
                  {level.label}
                </button>
              ))}
            </div>
          </div>
          <div className="gallery-filter-sheet-group">
            <label className="gallery-filter-sheet-label" htmlFor="gallery-subject-select">교과·주제</label>
            <div className="gallery-subject-select-wrap">
              <select
                id="gallery-subject-select"
                className="gallery-subject-select"
                value={subject}
                onChange={(e) => handleSubjectChange(e.target.value)}
              >
                {getLibrarySubjectFilters(schoolLevel).map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>
          {!loading && isListFresh && (
            <p className="gallery-filter-sheet-result" aria-live="polite">
              {listTotalCount > 0
                ? `조건에 맞는 자료 ${listTotalCount.toLocaleString('ko-KR')}개`
                : displayItems.length === 0 ? '조건에 맞는 자료가 없어요' : ''}
            </p>
          )}
        </div>
      </Dialog>

      {/* 자료 상세를 불러오는 동안: 닫을 수 없는 작은 다이얼로그 + 스피너 (모션에만 의존하지 않고 문구로도 안내) */}
      <Dialog
        align="center"
        dismissible={false}
        icon={<Spinner size={32} thickness={2} label="자료를 불러오는 중" />}
        open={detailLoading}
        size="sm"
        testId="gallery-detail-loading"
        title="자료를 불러오고 있어요"
      />

      <GalleryDetailModal
        item={detailItem}
        loading={Boolean(detailItem) && detailPreviewId === detailItem.id}
        onClose={closeDetail}
        onUse={handleUse}
        onLike={handleLike}
        onFavorite={handleFavorite}
        onDelete={handleDelete}
        onShare={handleShare}
        downloadBusy={isLibraryDownloading}
        downloadLabel={libraryDownloadLabel}
        onTrackDownload={(itemId) => { trackGalleryDownload(itemId).catch(() => {}); }}
        onDownload={async (url, itemId, contentType, title) => {
          const safeTitle = (title || 'whatquiz').replace(/[/\\:*?"<>|]/g, '_').slice(0, 50);
          const extMap = { ppt: '.pptx', worksheet: '.pdf', image: '.png' };
          const ext = contentType === 'other' ? '' : (extMap[contentType] || '');
          const filename = `${safeTitle}${ext}` || 'whatquiz-file';
          const downloadUrl = String(url || '').startsWith('http') || String(url || '').startsWith('data:')
            ? url
            : (url ? `${API_BASE_URL}${url}` : '');
          const result = await downloadLibraryFile({
            url: downloadUrl,
            filename,
            replayAction: downloadUrl
              ? { label: '다시 받기', onClick: () => downloadAndOpen(downloadUrl, filename) }
              : undefined,
          });
          if (result.ok && itemId) trackGalleryDownload(itemId).catch(() => {});
        }}
        onUpdate={async (itemId, updates) => {
          await updateGalleryItem(itemId, updates);
          updateItem(itemId, updates);
        }}
      />

      <MyContentDetailModal
        item={myContentDetail}
        isSharing={isSharingMyContent}
        onClose={closeMyContentDetail}
        onDetailLoaded={handleMyContentDetailLoaded}
        onShare={handleShareMyContent}
        onViewShared={(galleryId) => navigate(gallerySharePath(galleryId))}
        onContinueInChat={(myItem, resume = {}) => {
          setMyContentDetail(null);
          if (!myItem.conversation_id) return;
          const artifactQuery = resume.artifactId
            ? `&artifact=${encodeURIComponent(resume.artifactId)}`
            : '';
          navigate(`/chat?id=${encodeURIComponent(myItem.conversation_id)}${artifactQuery}`, {
            state: {
              libraryResume: {
                itemId: myItem.id,
                contentType: myItem.content_type,
                downloadUrl: myItem.download_url || null,
                filename: myItem.filename || null,
                title: myItem.title,
              },
            },
          });
        }}
        onUpdate={async (itemId, updates) => {
          await updateMyContent(itemId, updates);
          setLists((prev) => ({ ...prev, my: patchLibraryListItem(prev.my, itemId, updates) }));
          setMyContentDetail(prev => prev ? { ...prev, ...updates } : prev);
        }}
        onDelete={async (itemId) => {
          const ok = await openConfirm({
            title: '이 자료를 삭제할까요?',
            description: '삭제한 자료는 되돌릴 수 없어요',
            confirmLabel: '삭제하기',
            danger: true,
          });
          if (!ok) return;
          try {
            await deleteMyContent(itemId);
            setLists((prev) => ({ ...prev, my: removeLibraryListItem(prev.my, itemId) }));
            closeMyContentDetail();
          } catch (err) {
            showToast('삭제하지 못했어요. 잠시 후 다시 시도해주세요', 'error');
          }
        }}
      />

      <LoginPrompt
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        feature="라이브러리"
      />
    </>
  );
};

export default GalleryPage;
